import {CidIdMap} from 'notebook/notes/NoteConstants';
import {addCid} from 'notebook/store/StoreTransformUtil';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  FromServerStructuredTable,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
  ToServerStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import fillFormulaDefault from 'notebook/structured-tables/util/fillFormulaDefault';

/**
 * This function is called when receiving a StructuredTable from the server before we've placed it in our
 * Redux store or after we've initially created it from the insertion modal. Its main job is to add cids to
 * the table and its columns, and ensure those cids are stored in the cidIdMap. It also writes column default
 * formulas into cells the server stored without text, so every cell in the store has text.
 * The function mutates `serverStructuredTable` in place.
 */
export function prepareForStore<TTableMetadata, TRowMetadata>(
  serverStructuredTable: FromServerStructuredTable<TTableMetadata, TRowMetadata>,
  cidIdMap: CidIdMap
): void {
  addCid(serverStructuredTable, 'st', cidIdMap);
  // @ts-expect-error - The loop does some internal massaging, which is intended.
  serverStructuredTable.columns = serverStructuredTable.columns.map((column) => {
    // structuredTableColumn used to be dataTableColumn and use
    // DataTableColumn on the backend, so use dtcol as the cid
    // prefix for now.
    // @ts-expect-error - ignoring as the cid
    if (column.cid == null) {
      addCid(column, 'dtcol', cidIdMap);
    }

    const schemaAndColumnGroupArray: Array<SchemaAndColumnGroup> =
      serverStructuredTable.metadata.schemaMetadata
        .map((metadata) => {
          const schemaIdx = serverStructuredTable.schemas.findIndex(
            (schema) => schema.id === metadata.schemaId
          );
          const schema = serverStructuredTable.schemas[schemaIdx];
          if (!schema) {
            return null;
          }
          return {
            ...schema,
            columnGroup: metadata.group,
          };
        })
        .filter((schemaAndColumnGroup) => schemaAndColumnGroup != null) as Array<SchemaAndColumnGroup>;

    return getStructuredTableAdapters()[serverStructuredTable.kind].injectFrontEndMetadata({
      // @ts-expect-error - We've installed a cid immediately above
      column,
      schemas: schemaAndColumnGroupArray,
      tableMetadata: serverStructuredTable.metadata,
    });
  });
  // @ts-expect-error - The loop does some internal massaging, which is intended.
  serverStructuredTable.delegatedColumns = serverStructuredTable.delegatedColumns.map((column) => {
    // structuredTableColumn used to be dataTableColumn and use
    // DataTableColumn on the backend, so use dtcol as the cid
    // prefix for now.
    // @ts-expect-error - ignoring as the cid
    if (column.cid == null) {
      addCid(column, 'dtcol', cidIdMap);
    }

    const schemaAndColumnGroupArray: Array<SchemaAndColumnGroup> =
      serverStructuredTable.metadata.schemaMetadata
        .map((metadata) => {
          const schemaIdx = serverStructuredTable.schemas.findIndex(
            (schema) => schema.id === metadata.schemaId
          );
          const schema = serverStructuredTable.schemas[schemaIdx];
          if (!schema) {
            return null;
          }
          return {
            ...schema,
            columnGroup: metadata.group,
          };
        })
        .filter((schemaAndColumnGroup) => schemaAndColumnGroup != null) as Array<SchemaAndColumnGroup>;

    return getStructuredTableAdapters()[serverStructuredTable.kind].injectFrontEndMetadata({
      // @ts-expect-error - We've installed a cid immediately above
      column,
      schemas: schemaAndColumnGroupArray,
      tableMetadata: serverStructuredTable.metadata,
    });
  });
  serverStructuredTable.rows = serverStructuredTable.rows.map((row, rowIndex) => ({
    ...row,
    cells: row.cells.map((cell, columnIndex) =>
      fillFormulaDefault(cell, serverStructuredTable.columns, rowIndex, columnIndex)
    ),
  }));
}

// This function is called before sending a StructuredTable to the
// server
export function prepareForSave<TTableMetadata, TRowMetadata>(
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
  cidIdMap: CidIdMap
): ToServerStructuredTable<TTableMetadata, TRowMetadata> {
  const columns = structuredTable.columns.map((column) => {
    const {frontEndMetadata, ...columnWithoutMetadata} = column;
    return {...columnWithoutMetadata, id: cidIdMap[column.cid] as number};
  });
  const delegatedColumns = structuredTable.delegatedColumns.map((column) => {
    const {frontEndMetadata, ...columnWithoutMetadata} = column;
    return {...columnWithoutMetadata, id: cidIdMap[column.cid] as number};
  });
  return {
    ...structuredTable,
    id: cidIdMap[structuredTable.cid] as string,
    columns,
    delegatedColumns,
  };
}

export function prepareLegacyColumnForSave(
  column: StructuredTableColumn,
  cidIdMap: CidIdMap
): StructuredTableColumn {
  // TODO(TypeScript): We cast the `id` on the column to `any` to avoid overhauling various table interface
  // types. We should refactor RegistrationTable and TransferTableModel to separate out client, to-server,
  // and from-server variants, similar to DataTable.
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  return {...column, id: cidIdMap[column.cid] as any};
}
